import { LightbulbIcon } from "lucide-react";

import { Text } from "@/components/ui/text";
import type { InsightsAskResult } from "./InsightsQueryPanel";

type InsightsSummaryCardProps = {
  summary: NonNullable<InsightsAskResult["summary"]>;
  countColumnLabel?: string;
};

function formatShare(sharePercent: number): string {
  return Number.isInteger(sharePercent)
    ? `${sharePercent}%`
    : `${sharePercent.toFixed(1)}%`;
}

export function InsightsSummaryCard({
  summary,
  countColumnLabel = "Count",
}: InsightsSummaryCardProps) {
  return (
    <div className="flex flex-col gap-2">
      <Text size="2">{summary.narrative}</Text>
      {summary.caveats ? (
        <Text size="2" muted>
          {summary.caveats}
        </Text>
      ) : null}
      <div className="overflow-x-auto rounded-md border bg-background/80">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left border-b bg-muted/40">
              <th className="py-1.5 px-2 font-medium">Theme</th>
              <th className="py-1.5 px-2 font-medium tabular-nums w-16">
                {countColumnLabel}
              </th>
              <th className="py-1.5 px-2 font-medium tabular-nums w-14">
                Share
              </th>
              <th className="py-1.5 px-2 font-medium min-w-[8rem]">Examples</th>
            </tr>
          </thead>
          <tbody>
            {summary.themes.map((theme) => (
              <tr
                key={theme.theme}
                className="border-b border-border/50 last:border-0"
              >
                <td className="py-1.5 px-2 align-top font-medium">
                  {theme.theme}
                </td>
                <td className="py-1.5 px-2 align-top tabular-nums">
                  {theme.report_count}
                </td>
                <td className="py-1.5 px-2 align-top tabular-nums">
                  {formatShare(theme.share_percent)}
                </td>
                <td className="py-1.5 px-2 align-top text-muted-foreground line-clamp-2">
                  {theme.example_labels.join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function summaryErrorHint(error: string): string | null {
  if (error.includes("count column")) {
    return "Rephrase the question so SQL uses GROUP BY with COUNT(*) AS report_count (or comment_count).";
  }
  if (error.includes("2 distinct labels")) {
    return "Clustering needs at least two categories in the result. Widen the date range or inspect the raw breakdown below.";
  }
  if (error.includes("empty")) {
    return "Grouped labels were all blank — use COALESCE(NULLIF(TRIM(...), ''), '(none)') on the text column.";
  }
  if (error.includes("label column")) {
    return "Include one text column to group by (e.g. correction_reason or comment_text) plus a numeric count.";
  }
  if (/json|valid json/i.test(error)) {
    return "The model returned malformed JSON. Run the question again; the server will retry with a shorter summary format.";
  }
  return null;
}

type InsightsSummaryErrorProps = {
  error: string;
};

export function InsightsSummaryError({ error }: InsightsSummaryErrorProps) {
  const hint = summaryErrorHint(error);

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <LightbulbIcon className="size-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex flex-col gap-1 min-w-0">
          <Text size="2" className="font-medium">
            Theme summary not available
          </Text>
          <Text size="2">{error}</Text>
          {hint ? (
            <Text size="2" muted>
              {hint}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
}
